import { Button, Dialog, useMediaQuery } from "@material-ui/core"
import { useCallback, useEffect, useState } from "react"

import { theme } from "../../shared/theme"
import { AthlinksLogo } from "../../components/shared/AthlinksLogo"
import useReigniteMetadata from "../../lib/firebase/useReigniteMetadata"

import { useStyles } from "./styles"

type LocalStorageResponse = {
  response: 'notNow' | 'continue'
  expiration: number
}

const DIALOG_OPENING_DELAY = 10_000
const RESPONSE_EXPIRATION_TIME_IN_MS = 96 * 60 * 60 * 1000 // 96 hours
const STORAGE_KEY = 'mobileAppPopupResponse'

const LTMobileAppPromo = () => {
  const { isShowingMobileAppModal } = useReigniteMetadata()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const styles = useStyles()

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const saveUserResponse = (response: 'notNow' | 'continue') => {
    const now = new Date().getTime()
    const expirationTime = now + RESPONSE_EXPIRATION_TIME_IN_MS
    const userResponseData: LocalStorageResponse = {
      response,
      expiration: expirationTime
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userResponseData))
  }

  const onClickClose = () => {
    saveUserResponse('notNow')
    setIsDialogOpen(false)
  }

  const onClickContinue = () => {
    saveUserResponse('continue')
    setIsDialogOpen(false)
  }

  const checkLocalStorageResponse = useCallback(() => {
    const existingResponse = localStorage.getItem(STORAGE_KEY)

    if (!existingResponse) {
      return null
    }

    const parsedResponse: LocalStorageResponse = JSON.parse(existingResponse)
    const now = new Date().getTime()

    if (parsedResponse.expiration > now) {
      return parsedResponse.response
    }

    localStorage.removeItem(STORAGE_KEY)
    return null
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    const userResponse = checkLocalStorageResponse()

    if (isShowingMobileAppModal && !userResponse) {
      timer = setTimeout(() => {
        setIsDialogOpen(true)
      }, DIALOG_OPENING_DELAY)
    }

    return () => clearTimeout(timer)
  }, [checkLocalStorageResponse, isShowingMobileAppModal])

  return (
    <Dialog
      fullScreen={isMobile}
      fullWidth
      maxWidth='md'
      open={isDialogOpen}
      classes={{ paperFullWidth: styles.paperFullWidth }}
    >
      <div className={styles.backgroundImage}>
        <div className={styles.contentContainer}>
          <AthlinksLogo height='49px' fill='#fff' />

          <div className={styles.contentText}>
            <p><span>Good News - It's all in the app!</span></p>

            <p>You can now find, claim, and share race results in the Life Time app!</p>

            <p>Plus, get access to <span>expert-led training and recovery programs</span> to help you prep for your next big race — all in one place.</p>

            <p><span>No membership needed. Just download and go!</span></p>
          </div>

          <div className={styles.buttonsContainer}>
            <Button variant='outlined' onClick={onClickClose}>
              Not Now
            </Button>

            <a href='https://www.lifetime.life/digital-app.html' target='_blank' rel='noopener noreferrer'>
              <Button variant='contained' onClick={onClickContinue}>
                Continue To Life Time
              </Button>
            </a>
          </div>

          <p className={styles.footerText}>Already have an Athlinks account? Log in to the Life Time app with your same email and password.</p>
        </div>
      </div>
    </Dialog>
  )
}

export { LTMobileAppPromo }